import * as React from "react";
import { DatePicker, Input, Select, Radio, Col, Row, Button, Tabs, Form, SelectProps } from "antd";
import { observer } from "mobx-react";
import { withTranslation, WithTranslation } from "react-i18next";
import { taskGroupStore } from "../Stores/TaskGroupStore";
import { contactsStore } from "Stores/ContactsStore";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { tasksDataStore } from "../Stores/TasksDataStore";
import { I18N_NS } from "Config/i18n";
import { userInfoStore, taskModalStore, projectsDataStore } from "Stores";
import { taskSubmitStore } from "../Stores/TaskSubmitStore";
import { FormInstance } from "antd/es/form";
import { TaskFilesTable } from "./TaskFilesTable";
import { taskAttachmentsStore } from "../Stores/TaskAttachmentsStore";
import { RouterStore } from "Config/CustomBrowserRouter";
import { dayjs } from "Config/dayjs";
import { formatUsername } from "utils/formatUsername";
import { taskDataStore } from "../Stores";

const FormItem = Form.Item;
const Option = Select.Option;
const OptGroup = Select.OptGroup;
const RadioGroup = Radio.Group;
const RadioButton = Radio.Button;

interface TaskFormProps extends WithTranslation {}

interface TaskFormState {
  activeKey: string;
  isAccessVisible: boolean;
  accessException: "PIN" | "ACCESS_TYPE";
}

export const TaskForm = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<TaskFormProps, TaskFormState> {
      private formRef = React.createRef<FormInstance>();

      constructor(props: TaskFormProps) {
        super(props);
        this.state = {
          activeKey: "Task",
          isAccessVisible: false,
          accessException: null,
        };
      }

      componentDidMount() {
        (async () => {
          if (taskGroupStore.data.length < 1) {
            await taskGroupStore.fetch();
          }
        })();
      }

      handleRemoveFile = async (ids: string[]) => {
        ids.forEach((id) => {
          taskAttachmentsStore.deleteFile(id);
        });
        // await taskSubmitStore.editTask(taskSubmitStore.data);
      };

      handleSubmit = async (values) => {
        const { t } = this.props;
        this.setState({ accessException: null });

        if (values.startDate) {
          values.startDate = values.startDate.format("x");
        }
        if (values.dueDate) {
          values.dueDate = values.dueDate.format("x");
        }
        taskSubmitStore.update(values);
        try {
          await taskSubmitStore.createTask(taskSubmitStore.data);
          notificationSuccess(t("Success"), t("Task was created/updated"), "task-notification-created-updated");
          if (taskModalStore.from === "docs") {
            const type = taskSubmitStore.data.global ? "public" : "private";
            const { listId, id } = taskSubmitStore.data;
            RouterStore.push(`/project/${projectsDataStore.projectId}/tasks/${type}/list/${listId}/${id}`);
          }
          taskModalStore.toggleModal("task", true);
          taskSubmitStore.clearAll();
        } catch (err) {
          throw err;
        }
      };

      renderOptions = (options: API_TaskListData[]) => {
        const globalList = [],
          privateList = [],
          completeList = [
            <OptGroup key="2" label={this.props.t("Private task lists")}>
              {privateList}
            </OptGroup>,
          ];
        options.forEach((item) => {
          if (item.global) {
            globalList.push(
              <Option key={`${item.id}`} value={item.id.toString()}>
                {item.name}
              </Option>
            );
          } else {
            privateList.push(
              <Option key={`${item.id}`} value={item.id.toString()}>
                {item.name}
              </Option>
            );
          }
        });

        if (!userInfoStore.isRestrictedUser) {
          completeList.unshift(
            <OptGroup key="1" label={this.props.t("Public task lists")}>
              {globalList}
            </OptGroup>
          );
        }

        return completeList;
      };

      get attachmentsLabel() {
        let label = this.props.t("Attachments");
        if (taskAttachmentsStore.fileList.length) {
          label += ` (${taskAttachmentsStore.fileList.length})`;
        }

        return label;
      }

      // validateStartEnd = (rule, value) => {
      //   if (!value) {
      //     return Promise.resolve();
      //   }
      //   const startDate = this.formRef?.current.getFieldValue("startDate");
      //   const dueDate = this.formRef?.current.getFieldValue("dueDate");
      //   if (rule.field === "dueDate") {
      //     if (startDate) {
      //       if (startDate.isAfter(value, "day")) {
      //         return Promise.reject(
      //           this.props.t("Please pick an due date on the same day or later than the start date")
      //         );
      //       } else {
      //         return Promise.resolve();
      //       }
      //     } else {
      //       return Promise.resolve();
      //     }
      //   } else if (rule.field === "startDate") {
      //     if (dueDate) {
      //       if (dueDate.isBefore(value, "day")) {
      //         return Promise.reject(" ");
      //       } else {
      //         return Promise.resolve();
      //       }
      //     } else {
      //       return Promise.resolve();
      //     }
      //   }
      //   return Promise.resolve();
      // };

      disabledDate = (current) => {
        return dayjs().add(-1, "days") >= current;
      };

      handleValuesChange = (_, values) => {
        if (values.startDate && values.dueDate) {
          this.formRef.current?.validateFields(["startDate", "dueDate"]);
        }
      };

      get assigneeOptions(): SelectProps["options"] {
        const baseOptions: SelectProps["options"] = contactsStore.projectUsers
          .filter((item) => !item.restricted)
          .map((item) => ({
            key: `${item.id}`,
            disabled: !item.active || !item.projectMembershipActive,
            value: item.id.toString(),
            dataTestid: `task-form-assignee-option-${item.id}`,
            label: formatUsername({ firstName: item.firstName, surname: item.surname }),
          }));

        if (!baseOptions.some((item) => item.value === taskSubmitStore.data?.responsibleId?.toString())) {
          baseOptions.unshift({
            key: taskDataStore.data.responsibleId?.toString(),
            disabled: true,
            value: taskDataStore.data.responsibleId?.toString(),
            dataTestid: "task-form-assignee-option-0",
            label: taskDataStore.data.responsibleName,
          });
        }
        return baseOptions;
      }

      public render() {
        const { t } = this.props;
        const taskListOptions = this.renderOptions(taskGroupStore.data);

        return (
          <Form
            layout="vertical"
            ref={this.formRef}
            onFinish={this.handleSubmit}
            requiredMark={false}
            initialValues={{
              listId: (tasksDataStore.taskListId || -1).toString(),
              responsibleId: userInfoStore.id.toString(),
              priority: "NORMAL",
            }}
            onValuesChange={this.handleValuesChange}
          >
            <Tabs
              defaultActiveKey="Task"
              className="fullWidth mb-sm"
              tabPosition="top"
              items={[
                {
                  key: "Task",
                  label: <span data-testid="task-form-tab-task">{t("Task")}</span>,
                  children: (
                    <>
                      <FormItem
                        name="name"
                        label={t("Task subject")}
                        rules={[{ required: true, message: t("Please provide a name") }]}
                      >
                        <Input name="name" data-testid="task-form-subject-input" />
                      </FormItem>
                      <Row gutter={10} justify="space-between">
                        <Col xs={24} md={12}>
                          <FormItem
                            name="responsibleId"
                            label={t("Assignee")}
                            rules={[{ required: true, message: t("Please provide a responsible") }]}
                          >
                            <Select
                              data-testid="task-form-assignee-select"
                              showSearch={{
                                filterOption: (input, option) =>
                                  (option.props.children as string).toLowerCase().indexOf(input.toLowerCase()) >= 0,
                                optionFilterProp: "children",
                              }}
                              options={this.assigneeOptions}
                            />
                          </FormItem>
                        </Col>
                        <Col xs={24} md={12}>
                          <FormItem
                            name="priority"
                            label={t("Priority")}
                            rules={[{ required: true, message: t("Please select a priority") }]}
                          >
                            <RadioGroup className="fullWidth" data-testid="task-form-priority-group">
                              {/* Wrap each option: Radio.Button forwards data-testid to a hidden input (0×0). */}
                              {/* That hidden input breaks Cypress visibility checks. */}
                              <span data-testid="task-form-priority-low">
                                <RadioButton value="LOW">{t("low")}</RadioButton>
                              </span>
                              <span data-testid="task-form-priority-normal">
                                <RadioButton value="NORMAL">{t("normal")}</RadioButton>
                              </span>
                              <span data-testid="task-form-priority-high">
                                <RadioButton value="HIGH">{t("high")}</RadioButton>
                              </span>
                            </RadioGroup>
                          </FormItem>
                        </Col>
                      </Row>
                      <Row gutter={10} justify="space-between">
                        {/* <Col xs={24} md={12}>
                      <FormItem name="startDate" label={t("Start date")} rules={[{ validator: this.validateStartEnd }]}>
                        <DatePicker format="DD-MM-YYYY" style={{ width: `100%` }} />
                      </FormItem>
                    </Col> */}
                        <Col xs={24} md={12}>
                          <FormItem name="dueDate" label={t("Due date")}>
                            <DatePicker
                              format="DD-MM-YYYY"
                              style={{ width: `100%` }}
                              disabledDate={this.disabledDate}
                              data-testid="task-form-due-date-input"
                            />
                          </FormItem>
                        </Col>
                      </Row>
                      <FormItem name="description" label={t("Description")}>
                        <Input.TextArea data-testid="task-form-description-input" />
                      </FormItem>
                      <FormItem name="listId" label={t("Task lists")}>
                        <Select showSearch={true} optionFilterProp="children" data-testid="task-form-list-select">
                          {taskListOptions}
                        </Select>
                      </FormItem>
                    </>
                  ),
                },
                {
                  key: "Attachments",
                  label: <span data-testid="task-form-tab-attachments">{this.attachmentsLabel}</span>,
                  children: (
                    <TaskFilesTable
                      withButton={true}
                      canRemoveFile={true}
                      loading={taskAttachmentsStore.externalFilesLoading}
                      data={taskAttachmentsStore.fileList}
                      removeFile={this.handleRemoveFile}
                      addFile={(v) => taskSubmitStore.createTask(v)}
                    />
                  ),
                },
              ]}
            />
            <ButtonsRow>
              <Button
                type="primary"
                htmlType="submit"
                loading={taskSubmitStore.state === "LOADING"}
                data-testid="task-form-submit-button"
              >
                {t("Submit")}
              </Button>
            </ButtonsRow>
          </Form>
        );
      }
    }
  )
);
